/**
 * Free Payment Handler - Processes zero-value orders
 */
import type { CheckoutRequest } from "@/types/woocommerce.types";
import type { PaymentProcessingResult } from "@/plugins/payment-plugin-registry";
import { create_order } from "@/services/woocommerce/order-service";
import {
  PAYMENT_ERROR_CODES,
  type PAYMENT_ERROR_TYPES,
} from "@/lib/constants/payment-error-constants";
import { report_gateway_error } from "@/lib/observability/sentry-reporting";

/**
 * Processes free payment for zero-value orders
 * @param order_data - Order data containing billing and payment information
 * @returns Promise<PaymentProcessingResult> - Payment processing result
 */
export const free_payment_handler = async (
  order_data: CheckoutRequest,
): Promise<PaymentProcessingResult> => {
  try {
    // Prepare free payment order data
    const free_order_data: CheckoutRequest = {
      ...order_data,
      payment_method: "paytiko",
      payment_data: [{ key: "payment_method", value: "paytiko" }],
    };

    // Create the order using the order service
    const order_result = await create_order({
      order_data: free_order_data,
    });

    if (!order_result?.order_id) {
      // Propagate backend-provided code if it matches our standardized set,
      // otherwise fall back to ORDER_CREATION_FAILED
      const valid_codes = Object.values(PAYMENT_ERROR_CODES);
      const error_code: PAYMENT_ERROR_TYPES = valid_codes.includes(
        order_result.code as PAYMENT_ERROR_TYPES,
      )
        ? (order_result.code as PAYMENT_ERROR_TYPES)
        : PAYMENT_ERROR_CODES.ORDER_CREATION_FAILED;

      return {
        success: false,
        error_code,
        message: order_result.message || "Order creation failed.",
      };
    }

    return {
      success: true,
      order_id: order_result.order_id,
    };
  } catch (error) {
    report_gateway_error({
      error,
      gateway: "free",
      step: "payment_handler",
      message: "Free payment handler error",
      severity: "error",
    });
    return {
      success: false,
      error_code: PAYMENT_ERROR_CODES.PAYMENT_PROCESSING_ERROR,
    };
  }
};
